import axios from 'axios';
import { authService } from './authService';

export class ApiClient {
    constructor() {
        this.baseUrl = (process.env.REACT_APP_API_URL + '/api' || 'http://localhost:8081/api');
        this.logoutCallback = null; // New property to store the logout function

        this.client = axios.create({
            baseURL: this.baseUrl,
            // Don't set default Content-Type here - let each request set its own
            withCredentials: false // Use token-based auth, not cookies
        });

        // Add request interceptor for authentication
        this.client.interceptors.request.use(
            async (config) => {
                // Get a valid Firebase ID token (auto-refreshes when near expiry).
                const idToken = await authService.getValidIdToken({ minValiditySeconds: 300 });

                if (idToken) {
                    config.headers.Authorization = `Bearer ${idToken}`;
                } else {
                    console.warn("❌ No Google ID token available for request:", config.url);
                }

                return config;
            },
            (error) => Promise.reject(error)
        );

        // Add response interceptor for error handling
        this.client.interceptors.response.use(
            (response) => response,
            async (error) => {
                if (error.response) {
                    const { status, data } = error.response;
                    console.error(`API Error ${status}:`, data);

                    if (status === 401) {
                        console.warn('API request unauthorized (401)');
                        if (this.logoutCallback) this.logoutCallback();
                    }

                    if (status === 403) {
                        console.error('Permission denied (403) - insufficient privileges.');
                        // Optionally, you might want to logout or show a specific error for 403
                        // if (this.logoutCallback) this.logoutCallback();
                    }
                } else if (error.request) {
                    console.error('Network error - no response received.');
                } else {
                    console.error('Error setting up API request:', error.message);
                }

                return Promise.reject(error);
            }
        );
    }

    // New method to set the logout callback
    setLogoutCallback(callback) {
        this.logoutCallback = callback;
    }

    async get(url, config = {}) {
        const response = await this.client.get(url, config);
        const data = response?.data || response;
        if (data && typeof data === 'object' && !('success' in data)) {
            data.success = response.status >= 200 && response.status < 300;
        }
        return data;
    }

    async getBlobData(url, config = {}) {
        const response = await this.client.get(url, config);
        return response.data || null;
    }

    async post(url, data, config = {}) {
        const postConfig = {
            ...config,
            headers: {
                'Content-Type': 'application/json',
                ...(config.headers || {}),
            },
        };
        const response = await this.client.post(url, data, postConfig);
        const respData = response?.data || response;
        if (respData && typeof respData === 'object' && !('success' in respData)) {
            respData.success = response.status >= 200 && response.status < 300;
        }
        return respData;
    }

    /**
     * Upload multipart FormData. Ensures Content-Type is not forced so the browser
     * can set the correct multipart boundary header.
     * Returns response.data (consistent with other methods).
     */
    async upload(url, formData, config = {}) {
        // For FormData uploads, we need to ensure Content-Type is not set
        // so the browser can set the multipart boundary
        const uploadConfig = {
            ...config,
            headers: {
                ...config.headers,
            },
        };
        
        // Explicitly delete Content-Type to let browser set multipart boundary
        delete uploadConfig.headers['Content-Type'];
        delete uploadConfig.headers['content-type'];
        
        // Get auth token manually and add it
        try {
            const idToken = await authService.getValidIdToken({ minValiditySeconds: 300 });
            if (idToken) {
                uploadConfig.headers['Authorization'] = `Bearer ${idToken}`;
            }
        } catch (e) {
            console.warn('Failed to get auth token for upload:', e);
        }

        const response = await this.client.post(url, formData, uploadConfig);
        const respData = response?.data || response;
        if (respData && typeof respData === 'object' && !('success' in respData)) {
            respData.success = response.status >= 200 && response.status < 300;
        }
        return respData;
    }

    async put(url, data, config = {}) {
        const response = await this.client.put(url, data, config);
        const respData = response?.data || response;
        if (respData && typeof respData === 'object' && !('success' in respData)) {
            respData.success = response.status >= 200 && response.status < 300;
        }
        return respData;
    }

    async patch(url, data, config = {}) {
        const response = await this.client.patch(url, data, config);
        const respData = response?.data || response;
        if (respData && typeof respData === 'object' && !('success' in respData)) {
            respData.success = response.status >= 200 && response.status < 300;
        }
        return respData;
    }

    async delete(url, config = {}) {
        const response = await this.client.delete(url, config);
        const respData = response?.data || response;
        if (respData && typeof respData === 'object' && !('success' in respData)) {
            respData.success = response.status >= 200 && response.status < 300;
        }
        return respData;
    }
}

export const apiClient = new ApiClient();
